/**
 * @fileoverview Contact Roles Editor (MOR-676)
 *
 * Purpose: lets a practitioner manually override a job contact's roles after
 * import. Roles are normally derived from agent sources and rebuilt on every
 * reconcile, so a contact imported as e.g. `former_director` can't be used as
 * an active `director` for document generation. This control writes the role
 * set directly (locking it against reconcile) and offers a reset back to the
 * imported sources.
 *
 * It also manages org-defined **custom roles** (`jobContactRoles`): a separate,
 * always-manual vocabulary for classifications the built-in enum can't express
 * (e.g. "Tax Advisor"). Custom roles are never derived by reconcile, so they
 * are independent of the `rolesOverridden` mechanism. Any org member can pick
 * an existing custom role or create a new one inline.
 *
 * Behaviour:
 * - Renders the current built-in + custom role badges.
 * - "Edit roles" opens a multi-select Command popover over the built-in
 *   vocabulary (`ROLE_ORDER`) plus the org's custom roles, with an inline
 *   "Create <name>" affordance. Selection is staged locally and committed on
 *   Save (`setJobContactRoles` + `setJobContactCustomRoles`).
 * - When overridden, shows an "Overridden" chip and a Reset action
 *   (`clearJobContactRolesOverride`) that re-derives built-in roles from
 *   sources (custom roles are untouched).
 */

import { useMemo, useRef, useState } from "react";
import { Check, Pencil, Plus, Undo2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { toast } from "sonner";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/ui/composed/menus/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { cn } from "@/utils/misc";
import { ROLE_ORDER } from "@/lib/contact-roles";
import {
  ROLE_META,
  distinctRoles,
  resolveCustomRoleDisplays,
  type ContactRole,
  type CustomJobRole,
  type JobContactRow,
} from "./types";

/** Default badge colour for a role created inline from this editor. */
const DEFAULT_CUSTOM_ROLE_COLOR = "blue" as const;

interface ContactRolesEditorProps {
  contact: JobContactRow;
  disabled?: boolean;
}

export function ContactRolesEditor({
  contact,
  disabled,
}: ContactRolesEditorProps) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Set<ContactRole>>(new Set());
  const [selectedCustom, setSelectedCustom] = useState<
    Set<Id<"jobContactRoles">>
  >(new Set());
  const [query, setQuery] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const { data: customRoles } = useQuery(
    convexQuery(api.contacts.customRoles.listJobContactRoles, {
      orgId: contact.orgId,
    }),
  );
  const customById = useMemo(() => {
    const map = new Map<string, CustomJobRole>();
    for (const role of customRoles ?? []) map.set(role._id, role);
    return map;
  }, [customRoles]);

  const setRoles = useConvexMutation(api.contacts.mutations.setJobContactRoles);
  const setCustomRoles = useConvexMutation(
    api.contacts.mutations.setJobContactCustomRoles,
  );
  const clearOverride = useConvexMutation(
    api.contacts.mutations.clearJobContactRolesOverride,
  );
  const createRole = useConvexMutation(
    api.contacts.customRoles.createJobContactRole,
  );

  // Popover lives inside the contact-detail Sheet — portal back into the
  // dialog so scroll events don't get absorbed (mirrors ContactMergePicker).
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogContainer =
    triggerRef.current?.closest<HTMLElement>("[role='dialog']") ?? undefined;

  const currentRoles = useMemo(() => distinctRoles(contact), [contact]);
  const currentCustom = useMemo(
    () => resolveCustomRoleDisplays(contact.customRoleIds, customById),
    [contact.customRoleIds, customById],
  );

  const toggle = (role: ContactRole) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(role)) next.delete(role);
      else next.add(role);
      return next;
    });
  };

  const toggleCustom = (roleId: Id<"jobContactRoles">) => {
    setSelectedCustom((prev) => {
      const next = new Set(prev);
      if (next.has(roleId)) next.delete(roleId);
      else next.add(roleId);
      return next;
    });
  };

  const handleOpenChange = (next: boolean) => {
    // Seed the staged selection from the live roles each time we open.
    if (next) {
      setSelected(new Set(contact.roles ?? []));
      setSelectedCustom(new Set(contact.customRoleIds ?? []));
      setQuery("");
    }
    setOpen(next);
  };

  // Show a "Create <query>" affordance when the typed name doesn't already
  // exist as a custom role (case-insensitive).
  const trimmedQuery = query.trim();
  const canCreate =
    trimmedQuery.length > 0 &&
    !(customRoles ?? []).some(
      (r) => r.name.toLowerCase() === trimmedQuery.toLowerCase(),
    );

  const handleCreate = async () => {
    if (!canCreate || isCreating) return;
    setIsCreating(true);
    try {
      const roleId = await createRole({
        orgId: contact.orgId,
        name: trimmedQuery,
        color: DEFAULT_CUSTOM_ROLE_COLOR,
      });
      setSelectedCustom((prev) => new Set(prev).add(roleId));
      setQuery("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create role");
    } finally {
      setIsCreating(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Only write built-in roles when the selection actually changed.
      // Calling `setJobContactRoles` flips `rolesOverridden` to true (locking
      // the agent-derived roles against reconcile), so re-saving an unchanged
      // set purely because the user edited *custom* roles would lock the
      // built-in roles as an unwanted side effect.
      const prevBuiltIn = new Set(contact.roles ?? []);
      const builtInChanged =
        prevBuiltIn.size !== selected.size ||
        [...selected].some((r) => !prevBuiltIn.has(r));

      const ops: Array<Promise<null>> = [];
      if (builtInChanged) {
        ops.push(
          setRoles({
            contactId: contact._id,
            roles: ROLE_ORDER.filter((r) => selected.has(r)),
          }),
        );
      }
      // `setJobContactCustomRoles` no-ops server-side when unchanged, so it is
      // always safe to call and never touches `rolesOverridden`.
      ops.push(
        setCustomRoles({
          contactId: contact._id,
          customRoleIds: Array.from(selectedCustom),
        }),
      );
      await Promise.all(ops);
      setOpen(false);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to update roles",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      await clearOverride({ contactId: contact._id });
      toast.success("Roles reset to imported sources");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to reset roles");
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5 pt-1">
      {currentRoles.map((role) => {
        const meta = ROLE_META[role];
        return (
          <Badge key={role} color={meta.badgeColor} variant="soft" size="sm">
            {meta.label}
          </Badge>
        );
      })}
      {currentCustom.map((display) => (
        <Badge
          key={display.key}
          color={display.badgeColor}
          variant="soft"
          size="sm"
        >
          {display.label}
        </Badge>
      ))}
      {currentRoles.length === 0 && currentCustom.length === 0 && (
        <span className="text-xs text-muted-foreground/60">No roles</span>
      )}

      {contact.rolesOverridden && (
        <Badge
          color="amber"
          variant="outline"
          size="sm"
          title="Roles were manually overridden"
        >
          Overridden
        </Badge>
      )}

      {!disabled && (
        <Popover open={open} onOpenChange={handleOpenChange} modal>
          <PopoverTrigger asChild>
            <Button
              ref={triggerRef}
              variant="ghost"
              size="sm"
              className="h-6 gap-1 px-1.5 text-xs text-muted-foreground hover:text-foreground"
            >
              <Pencil className="h-3 w-3" />
              Edit roles
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[280px] p-0"
            align="start"
            onWheel={(e) => e.stopPropagation()}
            container={dialogContainer}
          >
            <Command
              filter={(value, search) => {
                const q = search.trim().toLowerCase();
                if (!q) return 1;
                return value.toLowerCase().includes(q) ? 1 : 0;
              }}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <CommandInput
                placeholder="Search or create a role…"
                value={query}
                onValueChange={setQuery}
              />
              <CommandList className="max-h-[280px] overscroll-contain">
                <CommandEmpty>No matching roles.</CommandEmpty>
                <CommandGroup heading="Built-in">
                  {ROLE_ORDER.map((role) => {
                    const meta = ROLE_META[role];
                    const isSelected = selected.has(role);
                    return (
                      <CommandItem
                        key={role}
                        value={meta.label}
                        onSelect={() => toggle(role)}
                        className="gap-2"
                      >
                        <div
                          className={cn(
                            "flex h-4 w-4 items-center justify-center rounded border",
                            isSelected
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-input",
                          )}
                        >
                          {isSelected && <Check className="h-3 w-3" />}
                        </div>
                        <span className="text-sm">{meta.label}</span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
                {(customRoles ?? []).length > 0 && (
                  <>
                    <CommandSeparator />
                    <CommandGroup heading="Custom">
                      {(customRoles ?? []).map((role) => {
                        const isSelected = selectedCustom.has(role._id);
                        return (
                          <CommandItem
                            key={role._id}
                            value={role.name}
                            onSelect={() => toggleCustom(role._id)}
                            className="gap-2"
                          >
                            <div
                              className={cn(
                                "flex h-4 w-4 items-center justify-center rounded border",
                                isSelected
                                  ? "border-primary bg-primary text-primary-foreground"
                                  : "border-input",
                              )}
                            >
                              {isSelected && <Check className="h-3 w-3" />}
                            </div>
                            <span className="text-sm">{role.name}</span>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </>
                )}
                {canCreate && (
                  <>
                    <CommandSeparator />
                    <CommandGroup>
                      <CommandItem
                        value={`__create__${trimmedQuery}`}
                        onSelect={handleCreate}
                        disabled={isCreating}
                        className="gap-2"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        <span className="text-sm">Create “{trimmedQuery}”</span>
                      </CommandItem>
                    </CommandGroup>
                  </>
                )}
              </CommandList>
              <div className="flex items-center justify-end gap-2 border-t border-border p-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setOpen(false)}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button size="sm" onClick={handleSave} disabled={isSaving}>
                  Save
                </Button>
              </div>
            </Command>
          </PopoverContent>
        </Popover>
      )}

      {!disabled && contact.rolesOverridden && (
        <Button
          variant="ghost"
          size="sm"
          className="h-6 gap-1 px-1.5 text-xs text-muted-foreground hover:text-foreground"
          onClick={handleReset}
        >
          <Undo2 className="h-3 w-3" />
          Reset to imported
        </Button>
      )}
    </div>
  );
}
